import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Check, RefreshCw, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import type { ChannelOrigin } from "../../api/types";
import { DataState, formatMoney, formatQuantity, formatTime, PageHeader } from "../../components/ui";
import { localDateTimeToUnix, toLocalDateTimeInput, validateUnixRange } from "../../utils/time";

const DAY = 24 * 60 * 60;

// CostsPage keeps every reporting dimension in the URL for reproducible views.
// 成本页把筛选条件保存在 URL，刷新和分享后仍能复现同一口径。
export function CostsPage() {
  const [search, setSearch] = useSearchParams();
  const now = useMemo(() => Math.floor(Date.now() / 1000), []);
  const startAt = numberParam(search, "start_at", now - 7 * DAY);
  const endAt = numberParam(search, "end_at", now + 1);
  const [timeDraft, setTimeDraft] = useState({ start_at: String(startAt), end_at: String(endAt) });
  const [customTimeOpen, setCustomTimeOpen] = useState(() => !presetDays(startAt, endAt));
  const granularity = search.get("granularity") === "day" ? "day" : "hour";
  const channelOrigin = normalizeOrigin(search.get("channel_origin"));
  const consumers = useQuery({ queryKey: ["consumers"], queryFn: api.consumers });
  const models = useQuery({ queryKey: ["models"], queryFn: api.models });
  const selectedConsumer = consumers.data?.data.find((item) => item.id === numberParam(search, "consumer_id", 0));
  const actualChannels = [...new Map((models.data?.data || []).flatMap((item) => item.channels).map((channel) => [channel.id, channel])).values()];
  useEffect(() => {
    if (search.has("start_at") && search.has("end_at") && search.has("granularity") && !search.has("consumer_key_id")) return;
    setSearch((current) => {
      const next = new URLSearchParams(current);
      next.delete("consumer_key_id");
      if (!next.has("start_at")) next.set("start_at", String(startAt));
      if (!next.has("end_at")) next.set("end_at", String(endAt));
      if (!next.has("granularity")) next.set("granularity", granularity);
      return next;
    }, { replace: true });
  }, [search, setSearch, startAt, endAt, granularity]);
  useEffect(() => setTimeDraft({ start_at: String(startAt), end_at: String(endAt) }), [startAt, endAt]);
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(search);
    params.delete("consumer_key_id");
    params.set("start_at", String(startAt));
    params.set("end_at", String(endAt));
    params.set("granularity", granularity);
    if (channelOrigin) params.set("channel_origin", channelOrigin); else params.delete("channel_origin");
    return params;
  }, [search, startAt, endAt, granularity, channelOrigin]);
  const usage = useQuery({
    queryKey: ["usage", queryParams.toString()],
    queryFn: () => api.usage(queryParams),
  });
  const report = usage.data?.data;
  const summary = report?.summary;
  const average = summary?.request_count ? Number(summary.total_cost) / summary.request_count : 0;
  const successRate = summary?.request_count ? summary.request_success_count / summary.request_count * 100 : 0;
  const maxCost = Math.max(1, ...(report?.series || []).map((item) => Number(item.total_cost)));
  const timeDirty = timeDraft.start_at !== String(startAt) || timeDraft.end_at !== String(endAt);
  const timeError = validateUnixRange(timeDraft.start_at, timeDraft.end_at, 366);
  const activePresetDays = customTimeOpen ? 0 : presetDays(startAt, endAt);

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "consumer_id") {
      next.delete("consumer_business_id");
    }
    if (key === "requested_model_id") next.delete("channel_id");
    setSearch(next, { replace: true });
  };
  const usePreset = (days: number) => {
    const next = new URLSearchParams(search);
    const range = { start_at: String(Math.floor(Date.now() / 1000) - days * DAY), end_at: String(Math.floor(Date.now() / 1000) + 1) };
    next.set("start_at", range.start_at);
    next.set("end_at", range.end_at);
    setTimeDraft(range);
    setCustomTimeOpen(false);
    setSearch(next, { replace: true });
  };
  const updateTimeDraft = (field: "start_at" | "end_at", value: string) => {
    setTimeDraft((current) => ({ ...current, [field]: value }));
  };
  const applyTime = () => {
    const next = new URLSearchParams(search);
    next.set("start_at", timeDraft.start_at);
    next.set("end_at", timeDraft.end_at);
    setSearch(next, { replace: true });
  };
  const resetFilters = () => {
    const next = new URLSearchParams(search);
    ["consumer_id", "consumer_business_id", "requested_model_id", "channel_id", "channel_origin"].forEach((key) => next.delete(key));
    const range = { start_at: String(Math.floor(Date.now() / 1000) - 7 * DAY), end_at: String(Math.floor(Date.now() / 1000) + 1) };
    next.set("start_at", range.start_at);
    next.set("end_at", range.end_at);
    next.set("granularity", "hour");
    setTimeDraft(range);
    setCustomTimeOpen(false);
    setSearch(next, { replace: true });
  };

  return (
    <>
      <PageHeader title="成本与用量" description="按业务方、业务标识、模型和实际渠道核对调用量与费用。" actions={<button className="secondary-button" onClick={() => usage.refetch()}><RefreshCw size={16} />刷新</button>} />
      <section className="filter-panel cost-filter-panel">
        <div className="filter-panel-heading">
          <strong>统计筛选</strong>
          <button type="button" className="secondary-button compact-action" onClick={resetFilters}><RotateCcw size={15} />重置筛选</button>
        </div>
        <div className="cost-filter-toolbar">
          <div className="filter-choice-group"><span>时间范围</span><div className="segmented-control" aria-label="成本时间范围">
            {[{ label: "24 小时", days: 1 }, { label: "7 天", days: 7 }, { label: "30 天", days: 30 }].map((item) => <button type="button" key={item.days} aria-pressed={activePresetDays === item.days} className={activePresetDays === item.days ? "active" : ""} onClick={() => usePreset(item.days)}>{item.label}</button>)}
            <button type="button" aria-pressed={customTimeOpen} className={customTimeOpen ? "active" : ""} onClick={() => setCustomTimeOpen(true)}>自定义</button>
          </div></div>
          {customTimeOpen ? <div className="custom-time-range">
            <label className="compact-field"><span>开始时间</span><input type="datetime-local" value={toLocalDateTimeInput(timeDraft.start_at)} onInput={(event) => updateTimeDraft("start_at", localDateTimeToUnix(event.currentTarget.value))} /></label>
            <span className="range-separator">至</span>
            <label className="compact-field"><span>结束时间</span><input type="datetime-local" value={toLocalDateTimeInput(timeDraft.end_at)} onInput={(event) => updateTimeDraft("end_at", localDateTimeToUnix(event.currentTarget.value))} /></label>
            <button type="button" className={timeDirty && !timeError ? "primary-button" : "secondary-button"} disabled={!timeDirty || Boolean(timeError)} onClick={applyTime}><Check size={15} />{timeError || "应用时间"}</button>
          </div> : null}
          <label className="compact-field narrow granularity-field"><span>粒度</span><select value={granularity} onChange={(event) => update("granularity", event.target.value)}><option value="hour">按小时</option><option value="day">按天</option></select></label>
          <div className="filter-choice-group"><span>渠道来源</span><div className="segmented-control" role="group" aria-label="渠道来源">
            {ORIGIN_OPTIONS.map((item) => <button type="button" key={item.value || "all"} aria-pressed={channelOrigin === item.value} className={channelOrigin === item.value ? "active" : ""} onClick={() => update("channel_origin", item.value)}>{item.label}</button>)}
          </div></div>
        </div>
        <div className="filter-row dimension-row">
          <FilterSelect label="业务方" value={search.get("consumer_id") || ""} onChange={(value) => update("consumer_id", value)} options={(consumers.data?.data || []).map((item) => ({ value: item.id, label: item.display_name }))} />
          <FilterSelect label="业务标识" value={search.get("consumer_business_id") || ""} onChange={(value) => update("consumer_business_id", value)} disabled={!selectedConsumer} options={(selectedConsumer?.businesses || []).map((item) => ({ value: item.id, label: item.business_identifier }))} />
          <FilterSelect label="请求模型" value={search.get("requested_model_id") || ""} onChange={(value) => update("requested_model_id", value)} options={(models.data?.data || []).map((item) => ({ value: item.id, label: item.display_name }))} />
          <FilterSelect label="实际渠道" value={search.get("channel_id") || ""} onChange={(value) => update("channel_id", value)} options={actualChannels.map((item) => ({ value: item.id, label: item.display_name }))} />
        </div>
      </section>

      <div className="date-range"><CalendarDays size={16} />{formatTime(startAt)} 至 {formatTime(endAt)} · 渠道来源 {originLabel(channelOrigin)} · 数据更新 {formatTime(report?.data_updated_at || 0)}</div>
      <DataState loading={usage.isLoading || consumers.isLoading || models.isLoading} error={usage.error || consumers.error || models.error}>
        <section className="metric-band cost-band">
          <div className="metric"><span>总费用</span><strong>{formatMoney(summary?.total_cost || 0)}</strong><small>{summary?.currency || "CNY"}</small></div>
          <div className="metric"><span>业务请求</span><strong>{(summary?.request_count || 0).toLocaleString()}</strong><small>成功率 {successRate.toFixed(1)}%</small></div>
          <div className="metric"><span>渠道尝试</span><strong>{(summary?.attempt_count || 0).toLocaleString()}</strong><small>含切换与降级</small></div>
          <div className="metric"><span>单请求均价</span><strong>{formatMoney(average)}</strong><small>总费用 / 请求量</small></div>
        </section>

        <section className="content-section usage-trend">
          <div className="section-heading"><div><h2>费用趋势</h2><p>{granularity === "hour" ? "小时" : "自然日"}汇总</p></div></div>
          {report?.series.length ? <div className={`trend-bars ${report.series.length <= 3 ? "sparse" : ""}`}>{report.series.map((point) => <div className="trend-column" key={point.bucket_start} title={formatTime(point.bucket_start)}><strong>{formatMoney(point.total_cost)}</strong><span style={{ height: `${Math.max(3, Number(point.total_cost) / maxCost * 100)}%` }} /><small>{granularity === "day" ? new Date(point.bucket_start * 1000).toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" }) : new Date(point.bucket_start * 1000).getHours().toString().padStart(2, "0")}</small></div>)}</div> : <div className="empty-inline">当前范围没有费用数据</div>}
        </section>

        <div className="report-grid">
          <ReportTable title="按业务方与业务标识" headers={["业务方 / 业务标识", "请求", "尝试", "费用"]} rows={(report?.business_breakdown || []).map((item) => [<><strong>{item.consumer_name || `业务方 #${item.consumer_id}`}</strong><small>{item.business_identifier || `业务 #${item.consumer_business_id}`}</small></>, item.request_count.toLocaleString(), item.attempt_count.toLocaleString(), formatMoney(item.total_cost)])} />
          <ReportTable title="按模型与渠道" headers={["请求模型", "实际渠道", "成功 / 失败", "计费量", "费用"]} rows={(report?.channel_breakdown || []).map((item) => [<><strong>{item.requested_model_key}</strong><small>{item.executed_model_key !== item.requested_model_key ? `执行 ${item.executed_model_key}` : "未跨模型"}</small></>, <><strong>{item.channel_name}</strong><small>{item.channel_code}</small></>, `${item.attempt_success_count} / ${item.attempt_failure_count}`, formatQuantity(item.billable_quantity), formatMoney(item.total_cost)])} />
        </div>
      </DataState>
    </>
  );
}

function FilterSelect({ label, value, options, disabled, onChange }: { label: string; value: string; options: Array<{ value: number; label: string }>; disabled?: boolean; onChange: (value: string) => void }) {
  return <label className="compact-field"><span>{label}</span><select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}><option value="">全部</option>{options.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>;
}

function ReportTable({ title, headers, rows }: { title: string; headers: string[]; rows: Array<Array<React.ReactNode>> }) {
  return <section className="content-section report-table"><div className="section-heading"><div><h2>{title}</h2><p>{rows.length} 项汇总</p></div></div>{rows.length ? <div className="table-wrap"><table><thead><tr>{headers.map((item) => <th key={item}>{item}</th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div> : <div className="empty-inline">当前筛选条件下没有记录</div>}</section>;
}

const ORIGIN_OPTIONS: Array<{ value: ChannelOrigin; label: string }> = [
  { value: "", label: "全部" },
  { value: "reverse", label: "逆向渠道" },
  { value: "official", label: "官方渠道" },
];

// normalizeOrigin keeps a hand-edited URL from sending an unsupported value that
// the backend would reject with a validation error.
// 规范化 URL 中的取值，避免手改地址栏后发出后端会拒绝的非法来源。
function normalizeOrigin(value: string | null): ChannelOrigin {
  return value === "reverse" || value === "official" ? value : "";
}

function originLabel(value: ChannelOrigin) {
  return ORIGIN_OPTIONS.find((item) => item.value === value)?.label || "全部";
}

function numberParam(params: URLSearchParams, key: string, fallback: number) {
  const value = Number(params.get(key));
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function presetDays(startAt: number, endAt: number) {
  const range = endAt - startAt;
  return [1, 7, 30].find((days) => Math.abs(range - days * DAY) < 120) || 0;
}
